import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ArrowUp, Check, LoaderCircle, Sparkles, Undo2 } from 'lucide-react';
import type { SmartSearchTurn } from '../../lib/smartSearchApi';

const GREEN = '#78BE20';
const MAX_TURNS = 8;
const ERROR_LINE = "Smart Search isn't available right now. Use the filters below.";

export interface SmartSearchAnswer {
  reply: string;
  /** One line describing what was applied. Its presence marks the search as applied (enables Undo). */
  summary?: string;
}

export interface SmartSearchBoxProps {
  /** Visible heading, also the textarea's accessible name. Say what the box is for. */
  heading: string;
  /** One line under the heading explaining what to type and what happens. */
  helpText: string;
  /** Label before the applied result line, e.g. "Filters set" renders "Filters set: Polos · 199 styles". */
  summaryLabel?: string;
  /** Set as the textarea's id so other controls can focus it. */
  inputId?: string;
  placeholder: string;
  /** Chips shown until the first question is asked. */
  examples: string[];
  /**
   * Called with the new question, the turns before it (user + assistant, at most 8)
   * and a signal the box aborts when a newer question starts or the box unmounts.
   * Throw to show the error line.
   */
  onAsk: (text: string, history: SmartSearchTurn[], signal: AbortSignal) => Promise<SmartSearchAnswer>;
  /** Shows an "Undo" button after a search that returned a summary. */
  onUndo?: () => void;
  /** sessionStorage key for the conversation, so follow-ups survive a reload. */
  storageKey: string;
  /** Start with an empty conversation: clears the stored one when the box mounts. */
  startFresh?: boolean;
  className?: string;
}

const isTurn = (t: unknown): t is SmartSearchTurn =>
  !!t && typeof t === 'object' &&
  ((t as SmartSearchTurn).role === 'user' || (t as SmartSearchTurn).role === 'assistant') &&
  typeof (t as SmartSearchTurn).content === 'string';

/** Only the question history is kept; the last reply and summary are never restored. */
function readStored(key: string): SmartSearchTurn[] {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.history) ? data.history.filter(isTurn).slice(-MAX_TURNS) : [];
  } catch {
    return [];
  }
}

function writeStored(key: string, history: SmartSearchTurn[] | null): void {
  try {
    if (history) window.sessionStorage.setItem(key, JSON.stringify({ history }));
    else window.sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable (private mode, quota): the box works without it */
  }
}

const SmartSearchBox: React.FC<SmartSearchBoxProps> = ({
  heading, helpText, summaryLabel = 'Filters set', inputId, placeholder, examples, onAsk, onUndo,
  storageKey, startFresh = false, className = '',
}) => {
  const uid = useId();
  const titleId = `${uid}-title`;
  const helpId = `${uid}-help`;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const controllerRef = useRef<AbortController | null>(null);

  // History survives a reload so follow-ups have context; reply and summary belong to this mount only.
  const [history, setHistory] = useState<SmartSearchTurn[]>(() => {
    if (startFresh) {
      writeStored(storageKey, null);
      return [];
    }
    return readStored(storageKey);
  });
  const [reply, setReply] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  // Undo only makes sense for a search applied in this visit: the page remembers
  // the previous query string in memory, which a reload loses.
  const [undoable, setUndoable] = useState(false);
  const [text, setText] = useState('');

  const historyRef = useRef(history);
  historyRef.current = history;

  // The input is disabled while a question is in flight, so focus waits for the re-render.
  const pendingFocus = useRef(false);
  useEffect(() => {
    if (!loading && pendingFocus.current) {
      pendingFocus.current = false;
      inputRef.current?.focus();
    }
  });

  useEffect(() => () => controllerRef.current?.abort(), []);

  const ask = useCallback(async (raw: string) => {
    const question = raw.trim();
    if (!question) return;

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setFailed(false);

    const before = historyRef.current;
    try {
      const answer = await onAsk(question, before, controller.signal);
      if (controller.signal.aborted) return;
      const next = [
        ...before,
        { role: 'user' as const, content: question },
        { role: 'assistant' as const, content: answer.reply },
      ].slice(-MAX_TURNS);
      const nextSummary = answer.summary || null;
      setHistory(next);
      setReply(answer.reply);
      setSummary(nextSummary);
      setUndoable(!!nextSummary);
      setText('');
      writeStored(storageKey, next);
    } catch {
      if (controller.signal.aborted) return; // superseded or unmounted, not a failure
      setFailed(true);
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setLoading(false);
      }
      pendingFocus.current = true;
    }
  }, [onAsk, storageKey]);

  const startOver = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setLoading(false);
    setHistory([]);
    setReply(null);
    setSummary(null);
    setFailed(false);
    setUndoable(false);
    setText('');
    writeStored(storageKey, null);
    pendingFocus.current = true;
  };

  const undo = () => {
    if (!onUndo) return;
    onUndo();
    setUndoable(false);
    setSummary(null);
    setReply('Back to your previous filters.');
    pendingFocus.current = true;
  };

  const started = history.length > 0 || reply !== null || failed;
  const showExamples = !started && !loading;
  const canSend = text.trim().length > 0;
  const fade = loading ? 'opacity-50' : '';

  return (
    <section
      aria-labelledby={titleId}
      className={`rounded-lg border border-[#78BE20]/35 bg-[#78BE20]/10 p-4 ${className}`}
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 shrink-0" style={{ color: GREEN }} aria-hidden />
        <h3 id={titleId} className="font-montserrat text-[16px] font-extrabold leading-tight text-black">{heading}</h3>
      </div>
      <p id={helpId} className="mt-1.5 font-poppins text-[13px] leading-snug text-black/70">{helpText}</p>

      <form
        className="relative mt-3"
        onSubmit={(e) => { e.preventDefault(); void ask(text); }}
      >
        <textarea
          id={inputId}
          ref={inputRef}
          rows={2}
          value={text}
          disabled={loading}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void ask(text);
            }
          }}
          placeholder={placeholder}
          aria-labelledby={titleId}
          aria-describedby={helpId}
          maxLength={500}
          className="block w-full resize-none rounded-md border border-black/20 bg-white py-2.5 pl-3 pr-12 font-poppins text-[14px] leading-snug text-black outline-none transition-colors placeholder:text-black/40 focus:border-[#78BE20] focus:ring-1 focus:ring-[#78BE20] disabled:cursor-wait disabled:opacity-70"
        />
        <button
          type="submit"
          disabled={!loading && !canSend}
          aria-label={loading ? 'Searching' : 'Send'}
          className={`absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
            loading || canSend ? 'bg-[#78BE20] text-white hover:bg-[#6aa81c]' : 'bg-black/10 text-black/35'
          }`}
        >
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden /> : <ArrowUp className="h-4 w-4" aria-hidden />}
        </button>
      </form>

      {showExamples && examples.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="mr-0.5 font-poppins text-[12px] font-medium text-black/60">Try:</span>
          {examples.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => { setText(''); void ask(ex); }}
              className="min-h-8 rounded-full border border-black/15 bg-white px-3 py-1 text-left font-poppins text-[12px] leading-snug text-black/80 transition-colors hover:border-[#78BE20] hover:text-black"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      <div aria-live="polite" className={started ? 'mt-3 space-y-2' : undefined}>
        {failed ? (
          <p className="font-poppins text-[13px] leading-snug text-black/70">{ERROR_LINE}</p>
        ) : (
          reply && (
            <p className={`flex items-start gap-2 font-poppins text-[13px] leading-snug text-black/80 transition-opacity ${fade}`}>
              <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: GREEN }} aria-hidden />
              <span>{reply}</span>
            </p>
          )
        )}
        {!failed && summary && (
          <p className={`flex items-start gap-1.5 rounded-md bg-white px-2.5 py-2 font-poppins text-[12px] leading-snug text-black transition-opacity ${fade}`}>
            <Check className="mt-[1px] h-3.5 w-3.5 shrink-0" style={{ color: GREEN }} aria-hidden />
            <span><span className="font-semibold">{summaryLabel}:</span> {summary}</span>
          </p>
        )}
      </div>

      {started && (
        <div className="mt-2 flex items-center gap-4">
          {onUndo && undoable && (
            <button
              type="button"
              onClick={undo}
              className="inline-flex items-center gap-1 font-poppins text-[12px] font-medium text-black/70 hover:text-black hover:underline"
            >
              <Undo2 className="h-3.5 w-3.5" aria-hidden />
              Undo
            </button>
          )}
          <button
            type="button"
            onClick={startOver}
            className="font-poppins text-[12px] font-medium text-black/70 hover:text-black hover:underline"
          >
            New search
          </button>
        </div>
      )}
    </section>
  );
};

export default SmartSearchBox;
